/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The signing algorithm of the key.
 */
export const GcpKmsKeyAlgorithm = {
  Rs256: "RS256",
  Es256: "ES256",
} as const;
/**
 * The signing algorithm of the key.
 */
export type GcpKmsKeyAlgorithm = OpenEnum<typeof GcpKmsKeyAlgorithm>;

/**
 * The cloud KMS provider of the key.
 */
export const GcpKmsKeyProvider = {
  AwsKms: "aws_kms",
  GcpKms: "gcp_kms",
} as const;
/**
 * The cloud KMS provider of the key.
 */
export type GcpKmsKeyProvider = OpenEnum<typeof GcpKmsKeyProvider>;

/**
 * A GCP KMS external key.
 */
export type GcpKmsKey = {
  /**
   * The signing algorithm of the key.
   */
  algorithm: GcpKmsKeyAlgorithm;
  /**
   * When the key was created.
   */
  createdAt: Date;
  /**
   * The Speakeasy identity (GCP service-account email or AWS principal ARN) the customer granted on the key for the key-policy / IAM-grant model. Not a secret.
   */
  customerGrantReference?: string | undefined;
  /**
   * The external credential Speakeasy uses to authenticate to the key.
   */
  externalCredentialId: string;
  /**
   * The ID of the external key.
   */
  id: string;
  /**
   * A human-readable name for the key.
   */
  name: string;
  /**
   * The organization that owns the key.
   */
  organizationId: string;
  /**
   * The cloud KMS provider of the key.
   */
  provider: GcpKmsKeyProvider;
  /**
   * The resource name of the GCP KMS key.
   */
  resourceName: string;
  /**
   * When the key was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const GcpKmsKeyAlgorithm$inboundSchema: z.ZodMiniType<
  GcpKmsKeyAlgorithm,
  unknown
> = openEnums.inboundSchema(GcpKmsKeyAlgorithm);

/** @internal */
export const GcpKmsKeyProvider$inboundSchema: z.ZodMiniType<
  GcpKmsKeyProvider,
  unknown
> = openEnums.inboundSchema(GcpKmsKeyProvider);

/** @internal */
export const GcpKmsKey$inboundSchema: z.ZodMiniType<GcpKmsKey, unknown> = z
  .pipe(
    z.object({
      algorithm: GcpKmsKeyAlgorithm$inboundSchema,
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      customer_grant_reference: z.optional(z.string()),
      external_credential_id: z.string(),
      id: z.string(),
      name: z.string(),
      organization_id: z.string(),
      provider: GcpKmsKeyProvider$inboundSchema,
      resource_name: z.string(),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "customer_grant_reference": "customerGrantReference",
        "external_credential_id": "externalCredentialId",
        "organization_id": "organizationId",
        "resource_name": "resourceName",
        "updated_at": "updatedAt",
      });
    }),
  );

export function gcpKmsKeyFromJSON(
  jsonString: string,
): SafeParseResult<GcpKmsKey, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => GcpKmsKey$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'GcpKmsKey' from JSON`,
  );
}
